import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  HttpClientModule,
  HTTP_INTERCEPTORS,
  HttpXsrfTokenExtractor
} from '@angular/common/http';

import {
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSortModule,
    MatTabsModule,
    MatToolbarModule,
    MatTooltipModule,
    MAT_DATE_FORMATS,
    MatExpansionModule
} from '@angular/material';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MomentDateModule } from "@angular/material-moment-adapter";
import { OverlayModule } from '@angular/cdk/overlay';
import { PortalModule } from '@angular/cdk/portal';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FroalaEditorModule, FroalaViewModule } from 'angular-froala-wysiwyg';
import 'hammerjs';

import './extensions/number.extension';
import './extensions/string.extension';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { AsperaService } from './aspera/aspera.service';
import { HeaderComponent } from './header/header.component';
import { ActiveTransferComponent } from './transfers/active-transfer.component';
import { NewTransferComponent } from './transfers/new-transfer.component';
import { TransferComponent } from './transfers/transfer.component';
import { TransfersService } from './transfers/transfers.service';
import { UsersService } from './users/users.service';
import { SelectFileDialogComponent } from './shared/select-file-dialog.component';
import { AuthComponent } from './auth/auth.component';
import { AuthCallbackComponent } from './auth/auth-callback.component';
import { AuthSignOutComponent } from './auth/auth-sign-out.component';
import { AuthService } from './auth/auth.service';
import { AuthGuard } from './auth/auth-guard.service';
import { LoadingComponent } from './shared/loading.component';
import { DownloadsComponent } from './downloads/downloads.component';
import { DownloadsService } from './downloads/downloads.service';
import { SuccessDialogComponent } from './shared/success-dialog.component';
import { ErrorDialogComponent } from './shared/error-dialog.component';
import { TimeOrDatePipe } from './shared/time-or-date.pipe';
import { HistoryComponent } from './history/history.component';
import { HistoryService } from './history/history.service';
import { RecentTemplatesComponent } from './templates/recent-templates.component';
import { LayoutComponent } from './layouts/layout.component';
import { SimpleLayoutComponent } from './layouts/simple-layout.component';
import { DownloadComponent } from './downloads/download.component';
import { RootGuard } from './root-guard.service';
import { TemplatesComponent } from './templates/templates.component';
import { TemplatesService } from './templates/templates.service';
import { WorkflowsComponent } from './workflows/workflows.component';
import { PageNavigationComponent } from './shared/page-navigation.component';
import { DynamicFieldsDirective } from './forms/dynamic-fields.directive';
import { DynamicRowsDirective } from './forms/dynamic-rows.directive';
import { DynamicSectionsDirective } from './forms/dynamic-sections.directive';
import { DynamicOverlaysDirective } from './forms/dynamic-overlays.directive';
import { FieldComponent } from './forms/field.component';
import { FormRowComponent } from './forms/form-row.component';
import { FormSectionComponent } from './forms/form-section.component';
import { TextFieldComponent } from './forms/text-field.component';
import { AutosizeTextFieldComponent } from './forms/autosize-text-field.component';
import { FilesFieldComponent } from './forms/files-field.component';
import { DateFieldComponent } from './forms/date-field.component';
import { TemplateSelectComponent } from './templates/template-select.component';
import { TokensFieldComponent } from './forms/tokens-field.component';
import { UsersFieldComponent } from './forms/users-field.component';
import { EmailsFieldComponent } from './forms/emails-field.component';
import { SenderFieldComponent } from './forms/sender-field.component';
import { AutocompleteSelectFieldComponent } from './forms/autocomplete-select-field.component';
import { AutocompleteTokensFieldComponent } from './forms/autocomplete-tokens-field.component';
import { WorkflowsService } from './workflows/workflows.service';
import { JellyrollFoldersFieldComponent } from './forms/jellyroll-folders-field.component';
import { JellyrollFilenameField } from './forms/jellyroll-filename-field.component';
import { LandingPadsFieldComponent } from './forms/landing-pads-field.component';
import { NavigationLinksComponent } from './layouts/navigation-links.component';
import { NrgTermsFieldComponent } from './forms/nrg-terms-field.component';
import { CheckboxFieldComponent } from './forms/checkbox-field.component';
import { TableFieldComponent } from './forms/table-field.component';
import { TimecodeFieldComponent } from './forms/timecode-field.component';
import { SelectFieldComponent } from './forms/select-field.component';
import { JumboSelectFieldComponent } from './forms/jumbo-select-field.component';
import { UnsavedDialogComponent } from './transfers/unsaved-dialog.component';
import { SaveAsDialogComponent } from './transfers/save-as-dialog.component';
import { SearchFieldComponent } from './forms/search-field.component';
import { DistributionListsComponent } from './users/distribution-lists.component';
import { DistributionListsFormComponent } from './users/distribution-lists-form.component';
import { DistributionListsDialogComponent } from './users/distribution-lists-dialog.component';
import { UsersAndListsFieldComponent } from './forms/users-and-lists-field.component';
import { EmailsAndListsFieldComponent } from './forms/emails-and-lists-field.component';
import { DistributionListsService } from './users/distribution-lists.service';
import { ConfirmDialogComponent } from './shared/confirm-dialog.component';
import { UnsavedGuard } from './transfers/unsaved-guard.service';
import { SaveDialogComponent } from './transfers/save-dialog.component';
import { AutocompleteComponent } from './forms/autocomplete.component';
import { SafeUrlPipe } from './shared/safe-url.pipe';
import { WorkflowNavigationComponent } from './workflows/workflow-navigation.component';
import { SelectDialogComponent } from './forms/select-dialog.component';
import { ExtendExpirationDialogComponent } from './transfers/extend-expiration-dialog.component';
import { DateHistoryFieldComponent } from './forms/date-history-field.component';
import { HistoryDialogComponent } from './forms/history-dialog.component';
import { RenameDialogComponent } from './transfers/rename-dialog.component';
import { RichTextFieldComponent } from './forms/rich-text-field.component';
import { NumberFieldComponent } from './forms/number-field.component';
import { IndexFieldComponent } from './forms/index-field.component';
import { AutoIndexFieldComponent } from './forms/auto-index-field.component';
import { ConvertFieldComponent } from './forms/convert-field.component';
import { ConvertDialogComponent } from './forms/convert-dialog.component';
import { RestoreDialogComponent } from './transfers/restore-dialog.component';
import { SendEmailDialogComponent } from './transfers/send-email-dialog.component';
import { NotesFieldComponent } from './forms/notes-field.component';
import { NotesDialogComponent } from './forms/notes-dialog.component';
import { UndoDialogComponent } from './transfers/undo-dialog.component';
import { TermsAndConditionsComponent } from './shared/terms-and-conditions.component';
import { FooterComponent } from './layouts/footer.component';
import { ShareHistoryComponent } from './history/share-history.component';
import { UserFieldComponent } from './forms/user-field.component';
import { HistoryGroupsComponent } from './history/history-groups.component';
import { CreateHistoryGroupComponent } from './history/create-history-group.component';
import { AuthInterceptor } from './auth/auth.interceptor';
import { StickyHeaderComponent } from './shared/sticky-header.component';
import { TimecodeStandardFieldComponent } from './forms/timecode-standard-field.component';
import { ActiveSessionsComponent } from './auth/active-sessions.component';
import { VersionService } from './version.service';
import { OffsetFieldComponent } from './forms/offset-field.component';
import { OffsetDialogComponent } from './forms/offset-dialog.component';
import { QCDashboardComponent } from './qc/qc-dashboard.component';
import { QCService } from './qc/qc.service';
import { RootComponent } from './root/root.component';
import { QCSubmitDialogComponent } from './qc/qc-submit-dialog.component';
import { TechSpecsFieldComponent } from './forms/tech-specs-field.component';
import { SettingsComponent } from './users/settings.component';
import { HttpClientXsrfModule, ɵg as HttpXsrfCookieExtractor } from "@angular/common/http";
import { XsrfInterceptor } from "./auth/xsrf.interceptor";
import { AocMigrationFaqComponent } from './help/aoc-migration-faq/aoc-migration-faq.component';
import { SupportService } from './shared/support.service';


export const DATE_FORMATS = {
  parse: {
        dateInput: ['l', 'D MMM YYYY', 'MMM D YYYY']
    },
    display: {
        dateInput: 'D MMM YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    ActiveTransferComponent,
    NewTransferComponent,
    TransferComponent,
    SelectFileDialogComponent,
    AuthComponent,
    AuthCallbackComponent,
    AuthSignOutComponent,
    LoadingComponent,
    DownloadsComponent,
    SuccessDialogComponent,
    ErrorDialogComponent,
    TimeOrDatePipe,
    HistoryComponent,
    RecentTemplatesComponent,
    LayoutComponent,
    SimpleLayoutComponent,
    DownloadComponent,
    TemplatesComponent,
    WorkflowsComponent,
    PageNavigationComponent,
    DynamicFieldsDirective,
    DynamicRowsDirective,
    DynamicSectionsDirective,
    DynamicOverlaysDirective,
    FormRowComponent,
    FormSectionComponent,
    TextFieldComponent,
    AutosizeTextFieldComponent,
    FilesFieldComponent,
    DateFieldComponent,
    TemplateSelectComponent,
    TokensFieldComponent,
    UsersFieldComponent,
    EmailsFieldComponent,
    SenderFieldComponent,
    AutocompleteSelectFieldComponent,
    AutocompleteTokensFieldComponent,
    JellyrollFoldersFieldComponent,
    JellyrollFilenameField,
    LandingPadsFieldComponent,
    NavigationLinksComponent,
    NrgTermsFieldComponent,
    CheckboxFieldComponent,
    TableFieldComponent,
    TimecodeFieldComponent,
    SelectFieldComponent,
    JumboSelectFieldComponent,
    UnsavedDialogComponent,
    SaveAsDialogComponent,
    SearchFieldComponent,
    DistributionListsComponent,
    DistributionListsFormComponent,
    DistributionListsDialogComponent,
    UsersAndListsFieldComponent,
    EmailsAndListsFieldComponent,
    ConfirmDialogComponent,
    SaveDialogComponent,
    AutocompleteComponent,
    SafeUrlPipe,
    WorkflowNavigationComponent,
    SelectDialogComponent,
    ExtendExpirationDialogComponent,
    DateHistoryFieldComponent,
    HistoryDialogComponent,
    RenameDialogComponent,
    RichTextFieldComponent,
    NumberFieldComponent,
    IndexFieldComponent,
    AutoIndexFieldComponent,
    ConvertFieldComponent,
    ConvertDialogComponent,
    RestoreDialogComponent,
    SendEmailDialogComponent,
    NotesFieldComponent,
    NotesDialogComponent,
    UndoDialogComponent,
    TermsAndConditionsComponent,
    FooterComponent,
    ShareHistoryComponent,
    UserFieldComponent,
    HistoryGroupsComponent,
    CreateHistoryGroupComponent,
    StickyHeaderComponent,
    TimecodeStandardFieldComponent,
    ActiveSessionsComponent,
    OffsetFieldComponent,
    OffsetDialogComponent,
    QCDashboardComponent,
    RootComponent,
    QCSubmitDialogComponent,
    TechSpecsFieldComponent,
    SettingsComponent,
    AocMigrationFaqComponent
  ],
  entryComponents: [
    SelectFileDialogComponent,
    SuccessDialogComponent,
    ErrorDialogComponent,
    TemplatesComponent,
    WorkflowsComponent,
    FormRowComponent,
    FormSectionComponent,
    TextFieldComponent,
    AutosizeTextFieldComponent,
    FilesFieldComponent,
    DateFieldComponent,
    TemplateSelectComponent,
    TokensFieldComponent,
    UsersFieldComponent,
    EmailsFieldComponent,
    SenderFieldComponent,
    AutocompleteSelectFieldComponent,
    AutocompleteTokensFieldComponent,
    JellyrollFoldersFieldComponent,
    JellyrollFilenameField,
    LandingPadsFieldComponent,
    NrgTermsFieldComponent,
    CheckboxFieldComponent,
    TableFieldComponent,
    TimecodeFieldComponent,
    SelectFieldComponent,
    JumboSelectFieldComponent,
    UnsavedDialogComponent,
    SaveAsDialogComponent,
    DistributionListsDialogComponent,
    UsersAndListsFieldComponent,
    EmailsAndListsFieldComponent,
    ConfirmDialogComponent,
    SaveDialogComponent,
    AutocompleteComponent,
    WorkflowNavigationComponent,
    SelectDialogComponent,
    ExtendExpirationDialogComponent,
    DateHistoryFieldComponent,
    HistoryDialogComponent,
    RenameDialogComponent,
    RichTextFieldComponent,
    NumberFieldComponent,
    IndexFieldComponent,
    AutoIndexFieldComponent,
    ConvertFieldComponent,
    ConvertDialogComponent,
    RestoreDialogComponent,
    SendEmailDialogComponent,
    NotesFieldComponent,
    NotesDialogComponent,
    UndoDialogComponent,
    TermsAndConditionsComponent,
    ShareHistoryComponent,
    UserFieldComponent,
    HistoryGroupsComponent,
    CreateHistoryGroupComponent,
    TimecodeStandardFieldComponent,
    OffsetFieldComponent,
    OffsetDialogComponent,
    QCSubmitDialogComponent,
    TechSpecsFieldComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    HttpClientXsrfModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MomentDateModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSortModule,
    MatTabsModule,
    MatToolbarModule,
    MatTooltipModule,
    MatSnackBarModule,
    OverlayModule,
    PortalModule,
    AppRoutingModule,
    FroalaEditorModule.forRoot(),
    FroalaViewModule.forRoot(),
    MatExpansionModule
  ],
  providers: [
    AsperaService,
    AuthService,
    AuthGuard,
    DownloadsService,
    HistoryService,
    RootGuard,
    TransfersService,
    UsersService,
    TemplatesService,
    WorkflowsService,
    DistributionListsService,
    UnsavedGuard,
    VersionService,
    QCService,
    SupportService,
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: XsrfInterceptor, multi: true },
    { provide: HttpXsrfTokenExtractor, useClass: HttpXsrfCookieExtractor }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }



// WEBPACK FOOTER //
// ./src/app/app.module.ts